/**
 * <yg-color-siblings> — aynı modelin diğer renkleri.
 *
 * Satırın ASIL kaynağı sunucudur: blocks/yg-color-siblings.liquid, ürünün
 * custom.renk_kardesleri metafield'ı doluysa bütün renkleri doğrudan Liquid'de
 * çizer. O durumda bu dosya sadece fareyle üzerine gelinen rengin sayfasını
 * önyükler; hiç istek atmaz.
 *
 * Metafield yoksa (ERP yeni ürün ekledi, toplu doldurma henüz çalışmadı) yedek
 * yol devreye girer: stil koduyla search.yg-siblings görünümü çekilir ve satır
 * doldurulur. Yanıt sessionStorage'da tutulur — bir modelin bütün renk
 * sayfaları aynı stil kodunu sorguladığı için yanıt birebir aynıdır.
 */
const YG_SIB_CACHE_PREFIX = 'yg-sib:';
const YG_SIB_TTL = 10 * 60 * 1000; // 10 dk: tükendi bilgisi çok bayatlamasın

if (!customElements.get('yg-color-siblings')) {
  customElements.define(
    'yg-color-siblings',
    class YgColorSiblings extends HTMLElement {
      #controller = null;
      #prefetched = new Set();

      connectedCallback() {
        this.list = this.querySelector('[data-yg-siblings-list]');
        if (!this.list) return;

        this.addEventListener('pointerenter', this.#handlePointerEnter, { capture: true });
        this.addEventListener('focusin', this.#handlePointerEnter, { capture: true });

        // Sunucu zaten bütün renkleri çizdiyse yapacak bir şey yok.
        if (this.dataset.serverRendered === 'true') return;

        const code = this.dataset.code;
        if (!code) return;

        // Controller her bağlantıda yeniden kurulur; sınıf alanı olarak bir kez
        // kurulsaydı disconnect sonrası yeniden bağlanmada sinyal zaten abort
        // edilmiş olur ve istek sessizce düşerdi.
        this.#controller = new AbortController();

        const cached = this.#readCache(code);
        if (cached) {
          this.#reveal(this.#paint(cached.html));
          if (Date.now() - cached.t > YG_SIB_TTL) this.#load(code, { sessiz: true });
        } else {
          this.#load(code);
        }
      }

      disconnectedCallback() {
        this.removeEventListener('pointerenter', this.#handlePointerEnter, { capture: true });
        this.removeEventListener('focusin', this.#handlePointerEnter, { capture: true });
        this.#controller?.abort();
      }

      #handlePointerEnter = (event) => {
        const link = event.target?.closest?.('a[data-yg-sibling]');
        if (!link || link.getAttribute('aria-current') === 'page') return;
        const href = link.getAttribute('href');
        if (!href || this.#prefetched.has(href)) return;
        this.#prefetched.add(href);
        const tag = document.createElement('link');
        tag.rel = 'prefetch';
        tag.as = 'document';
        tag.href = href;
        document.head.appendChild(tag);
      };

      #readCache(code) {
        try {
          const parsed = JSON.parse(sessionStorage.getItem(`${YG_SIB_CACHE_PREFIX}${code}`));
          return parsed?.html ? parsed : null;
        } catch {
          return null;
        }
      }

      #writeCache(code, html) {
        try {
          sessionStorage.setItem(
            `${YG_SIB_CACHE_PREFIX}${code}`,
            JSON.stringify({ html, t: Date.now() })
          );
        } catch {
          // Kota dolabilir; önbellek olmadan da çalışır.
        }
      }

      async #load(code, { sessiz = false } = {}) {
        const root = window.Shopify?.routes?.root ?? '/';
        const url = `${root}search?q=${encodeURIComponent(code)}&type=product&view=yg-siblings`;

        try {
          const response = await fetch(url, { signal: this.#controller.signal });
          if (!response.ok) return;

          const html = await response.text();

          // Şablon canlı temada yoksa Shopify 404 değil, 200 OK ile TAM arama
          // sayfası döndürüyor (canlıda ölçüldü: 392.390 bayt). response.ok bunu
          // yakalamaz; boyut ve işaret kontrolü yakalar.
          if (html.length > 65536) return;

          const bulunan = this.#paint(html);
          this.#reveal(bulunan);
          if (bulunan >= 1) this.#writeCache(code, html);
        } catch (error) {
          if (!sessiz && error.name !== 'AbortError') console.warn('[yg-color-siblings]', error);
        }
      }

      /**
       * Satırı yalnız GERÇEKTEN kardeş varsa açar.
       *
       * Yedek yolda Liquid kabuğu hidden basar. Tek sonuç ürünün kendisidir;
       * kendi görselini tek başına göstermek bilgi vermez, üstelik Horizon'un
       * "Renk X" satırı bu üründe ekranda kaldığı için sütunda iki kez renk
       * yazardı. Sunucu render'ında kabuk zaten hidden değildir.
       */
      #reveal(sayi) {
        if (sayi >= 2) this.hidden = false;
      }

      /** Gelen HTML'i listeye basar, basılan kardeş sayısını döner. */
      #paint(html) {
        const parsed = new DOMParser().parseFromString(html, 'text/html');

        // Doğru şablondan geldiğini doğrula (yukarıdaki nota bak).
        if (!parsed.querySelector('[data-yg-siblings-ok]')) return 0;

        const items = parsed.querySelectorAll('[data-yg-sibling]');
        if (!items.length) return 0;

        this.list.replaceChildren(...items);

        const current = this.list.querySelector(
          `[data-handle="${CSS.escape(this.dataset.current ?? '')}"]`
        );
        if (current) {
          current.classList.add('yg-siblings__item--current');
          current.setAttribute('aria-current', 'page');
        }
        return items.length;
      }
    }
  );
}
